import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { OrdersAPI } from '../services/api';
import ShippingMethodSelector from './checkout/shipping/ShippingMethodSelector';
import ContactForm from './checkout/ContactForm';
import PaymentMethodSelector from './checkout/payment/PaymentMethodSelector';
import OrderNotes from './checkout/OrderNotes';
import OrderSummary from './checkout/OrderSummary';
import OrderSuccess from './checkout/OrderSuccess';

import usePaymentMethods from '../hooks/usePaymentMethods';
import useShippingOptions from '../hooks/useShippingOptions';
import useShippingLocations from '../hooks/useShippingLocations';
import useCheckoutForm from '../hooks/useCheckoutForm';

const Checkout = () => {
  const { cart, dispatch } = useCart();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [preventNavigation, setPreventNavigation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  
  useEffect(() => {
    // Only navigate away if cart is empty AND we're not showing the order success screen
    if ((!cart.items || cart.items.length === 0) && !orderSuccess && !preventNavigation) {
      navigate('/');
    } else {
      setIsLoading(false);
    }
  }, [cart.items, navigate, orderSuccess, preventNavigation]);
  
  const { 
    formData,
    handleInputChange,
    getAddressField,
    validateForm,
    orderError,
    setOrderError,
    SPECIAL_SHIPPING_INSTANCE_ID,
    SPECIAL_SHIPPING_INSTANCE_ID_2,
    PICKUP_SHIPPING_INSTANCE_ID,
    ADDRESS_SHIPPING_INSTANCE_ID
  } = useCheckoutForm();
  
  const { 
    paymentMethods, 
    selectedPayment, 
    loading: loadingPaymentMethods, 
    handlePaymentChange 
  } = usePaymentMethods();
  
  const { 
    shippingOptions, 
    loading: loadingShipping, 
    handleSelectShippingMethod 
  } = useShippingOptions(formData.country);
  
  const shippingLocations = useShippingLocations(cart.shippingMethod?.instance_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидиране на формуляра
    if (!validateForm(selectedPayment, shippingLocations)) {
      return;
    }
    
    setLoading(true);
    setPreventNavigation(true); // Prevent navigation when order processing starts
    
    try {
      // Подготовка на данните за поръчката
      const orderData = {
        payment_method: selectedPayment,
        set_paid: selectedPayment !== 'bacs', // Маркиране като НЕПЛАТЕНО ако е банков превод
        status: selectedPayment === 'bacs' ? 'on-hold' : 'processing', // Задаване на поръчки с банков превод като изчакващи
        billing: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address_1: getAddressField('address', shippingLocations),
          city: getAddressField('city', shippingLocations),
          postcode: getAddressField('postcode', shippingLocations),
          country: getAddressField('country', shippingLocations)
        },
        shipping: {
          address_1: getAddressField('address', shippingLocations),
          city: getAddressField('city', shippingLocations),
          postcode: getAddressField('postcode', shippingLocations),
          country: getAddressField('country', shippingLocations)
        },
        shipping_method: cart.shippingMethod?.instance_id,
        customer_note: formData.notes,
        items: cart.items,
        coupon_code: cart.coupon?.code
      };
      
      // Създаване на поръчката
      const response = await OrdersAPI.createOrder(orderData);
      
      // Обработка на успешна поръчка
      if (response.success) {
        console.log('Order success:', response, 'Payment method:', selectedPayment, 'Shipping method:', cart.shippingMethod);
        setOrderSuccess(true);
        setOrderId(response.order.id);
        
        // Изчистване на кошницата
        dispatch({ type: 'CLEAR_CART' });
      }
    } catch (error) {
      console.error('Грешка при създаване на поръчка:', error);
      setOrderError('Неуспешно създаване на поръчка. Моля, опитайте отново.');
    } finally {
      setLoading(false);
    }
  };
  
  // Връщане по-рано, ако все още се зарежда или проверява кошницата
  if (isLoading) {
    return <div className="max-w-7xl mx-auto px-5 py-8 flex justify-center">
      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }
  
  if (orderSuccess) {
    return <OrderSuccess orderId={orderId} selectedPayment={selectedPayment} />;
  }
  
  return (
    <div className="max-w-7xl mx-auto px-5 py-8">
      <h2 className="text-2xl font-bold mb-6">Плащане</h2>
      
      {orderError && (
        <div className="bg-red-50 text-red-600 p-4 mb-6 rounded-md border border-red-200">
          {orderError}
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <form className="lg:col-span-2 flex flex-col gap-8" onSubmit={handleSubmit}>
          <ContactForm 
            formData={formData} 
            handleInputChange={handleInputChange} 
          />
          
          <ShippingMethodSelector
            loadingShipping={loadingShipping}
            shippingOptions={shippingOptions}
            cart={cart}
            handleSelectShippingMethod={handleSelectShippingMethod}
            // За Econt (метод 2)
            selectedCityId={shippingLocations.selectedCityId}
            setSelectedCityId={shippingLocations.setSelectedCityId}
            cities={shippingLocations.cities}
            filteredOffices={shippingLocations.filteredOffices}
            selectedOffice={shippingLocations.selectedOffice}
            setSelectedOffice={shippingLocations.setSelectedOffice}
            // За Speedy (метод 3)
            selectedSiteId={shippingLocations.selectedSiteId}
            setSelectedSiteId={shippingLocations.setSelectedSiteId}
            sites={shippingLocations.sites}
            filteredSpeedy2Offices={shippingLocations.filteredSpeedy2Offices}
            selectedSpeedy2Office={shippingLocations.selectedSpeedy2Office}
            setSelectedSpeedy2Office={shippingLocations.setSelectedSpeedy2Office}
            // За доставка до адрес (метод 5)
            formData={formData}
            handleInputChange={handleInputChange}
            // Константи
            SPECIAL_SHIPPING_INSTANCE_ID={SPECIAL_SHIPPING_INSTANCE_ID}
            SPECIAL_SHIPPING_INSTANCE_ID_2={SPECIAL_SHIPPING_INSTANCE_ID_2}
            PICKUP_SHIPPING_INSTANCE_ID={PICKUP_SHIPPING_INSTANCE_ID}
            ADDRESS_SHIPPING_INSTANCE_ID={ADDRESS_SHIPPING_INSTANCE_ID}
          />
          
          <PaymentMethodSelector
            loadingPaymentMethods={loadingPaymentMethods}
            paymentMethods={paymentMethods}
            selectedPayment={selectedPayment}
            handlePaymentChange={handlePaymentChange}
          />
          
          <OrderNotes 
            notes={formData.notes}
            handleInputChange={handleInputChange}
          />
          
          <button 
            type="submit"
            disabled={loading || !cart.shippingMethod || loadingShipping}
            className={`py-4 text-white text-lg font-bold rounded-md transition ${
              loading || !cart.shippingMethod || loadingShipping
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary hover:bg-primary/80'
            }`}
          >
            {loading ? 'Обработка...' : 'Направи поръчка'}
          </button>
        </form>
        
        <OrderSummary cart={cart} />
      </div>
    </div>
  );
};

export default Checkout; 